"use client";

import React from "react"

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { createClient } from "@/lib/supabase/client";
import { createLoginFlow, SESSION_CHECK_UNAVAILABLE_MESSAGE } from "@/lib/auth/login-flow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [idleLogout, setIdleLogout] = useState(false);
  const [sessionWarning, setSessionWarning] = useState<string | null>(null);
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);
  useEffect(() => {
    setIdleLogout(new URLSearchParams(window.location.search).get("reason") === "idle");
  }, []);
  const redirectToDashboard = useCallback(() => {
    if (typeof window !== "undefined") {
      window.location.assign("/selection");
      return;
    }

    router.replace("/selection");
    router.refresh();
  }, [router]);

  const loginFlow = useMemo(() => supabase ? createLoginFlow(supabase, {
    onError: setError,
    onLoading: setIsLoading,
    onNavigate: redirectToDashboard,
    onWarning: setSessionWarning,
  }) : null, [supabase, redirectToDashboard]);

  useEffect(() => {
    const unavailable = new URLSearchParams(window.location.search).get("reason") === "session-check-unavailable";
    if (unavailable) setSessionWarning(SESSION_CHECK_UNAVAILABLE_MESSAGE);
    return loginFlow?.start({ resumeSession: !unavailable });
  }, [loginFlow]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIdleLogout(false);
    const url = new URL(window.location.href);
    if (url.searchParams.getAll("reason").includes("idle")) {
      url.searchParams.delete("reason", "idle");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    if (!loginFlow) {
      setError("Autenticação não configurada. Por favor, configure as variáveis de ambiente do Supabase.");
      return;
    }
    await loginFlow.signIn(email, password);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            <Image
              src="/logo-own.svg"
              alt="OWN Logo"
              width={72}
              height={46}
              className="h-[46px] w-[72px] object-contain"
              priority
            />
          </div>
          <CardTitle className="text-2xl font-bold">OWN</CardTitle>
          <CardDescription>
            Entre com suas credenciais para acessar o sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {idleLogout && (
              <p role="status" className="text-sm text-orange-primary">
                Sua sessão foi encerrada por inatividade. Entre novamente para continuar.
              </p>
            )}
            {sessionWarning && (
              <p role="status" className="text-sm text-orange-primary">{sessionWarning}</p>
            )}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {"Usuários são criados apenas por administradores."}
              <br />
              {"Entre em contato caso precise de acesso."}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
